import { AlertBanner } from '@/components/common/AlertBanner'
import { type HPOSuggestion, type HPOTerm } from '@/components/common/HPOSearch'
import { HPOSearchModal } from '@/components/common/HPOSearchModal'
import { Modal, ModalFooter } from '@/components/common/Modal'
import { useAutosave } from '@/components/common/useAutosave'
import type { IndicationsData } from '@/components/order/OrderTypes'
import { Close, Plus } from 'flowbite-react-icons/outline'
import { useEffect, useRef, useState } from 'react'

interface IndicationsModalProps {
  isOpen: boolean
  onClose: () => void
  initialData: IndicationsData
  onSave: (data: IndicationsData) => void
  generatedAt?: string | null
  /**
   * When provided, the footer renders a brand "Continue to …" CTA (label from
   * `continueLabel`) that closes this modal and advances to the next order
   * section modal. Indications autosave, so continuing persists edits just
   * like Done.
   */
  onContinue?: () => void
  continueLabel?: string
  /**
   * When true, the modal opens with the Add Phenotypes (HPO search) step
   * already open — used by the card's secondary "Add Phenotypes" header button
   * so adding phenotypes is a single click. Routing through this modal (rather
   * than mounting HPOSearchModal standalone) keeps the modal's autosave intact.
   */
  openPhenotypeSearchOnOpen?: boolean
}

function sortPhenotypes(phenotypes: HPOTerm[]): HPOTerm[] {
  const typeOrder = { key: 0, additional: 1, excluded: 2 }
  return [...phenotypes].sort((a, b) => {
    const typeCompare = typeOrder[a.type] - typeOrder[b.type]
    if (typeCompare !== 0) return typeCompare
    return a.name.localeCompare(b.name)
  })
}

export function IndicationsModal({
  isOpen,
  onClose,
  initialData,
  onSave,
  generatedAt,
  onContinue,
  continueLabel = 'Continue',
  openPhenotypeSearchOnOpen = false,
}: IndicationsModalProps) {
  const [phenotypes, setPhenotypes] = useState<HPOTerm[]>([])
  const [searchModalOpen, setSearchModalOpen] = useState(false)
  const wasOpenRef = useRef(false)
  const { lastSaveTime, isSaving } = useAutosave({ phenotypes }, onSave, 500, isOpen)

  // Snapshot pre-edit data when the modal opens so Discard reverts to that
  // baseline rather than the latest autosaved state. (Autosave mutates canonical
  // mid-session, which would otherwise make `initialData` reflect the unsaved
  // edits by the time Discard fires.)
  const initialDataRef = useRef(initialData)
  initialDataRef.current = initialData
  const openBaselineRef = useRef<IndicationsData>(initialData)
  const openPhenotypeSearchOnOpenRef = useRef(openPhenotypeSearchOnOpen)
  openPhenotypeSearchOnOpenRef.current = openPhenotypeSearchOnOpen

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      openBaselineRef.current = initialDataRef.current
      setPhenotypes(sortPhenotypes(initialDataRef.current.phenotypes))
      // When launched from the card's "Add Phenotypes" shortcut, jump straight
      // to the HPO search step (still inside this modal, so autosave applies).
      setSearchModalOpen(openPhenotypeSearchOnOpenRef.current)
      wasOpenRef.current = true
    }
    if (!isOpen) {
      wasOpenRef.current = false
      setSearchModalOpen(false)
    }
  }, [isOpen])

  const handleClose = () => {
    onClose()
  }

  const handleDiscard = () => {
    onSave(openBaselineRef.current)
    onClose()
  }

  const handleContinue = () => {
    onContinue?.()
    onClose()
  }

  const handleSearchModalSelect = (suggestion: HPOSuggestion) => {
    const newTerm: HPOTerm = {
      id: suggestion.id,
      name: suggestion.name,
      hpoCode: suggestion.hpoCode,
      type: suggestion.type || 'key',
    }
    setPhenotypes([...phenotypes, newTerm])
  }

  const handleRemove = (termId: string) => {
    setPhenotypes(phenotypes.filter((p) => p.id !== termId))
  }

  const handleTypeChange = (termId: string, newType: 'key' | 'additional' | 'excluded') => {
    setPhenotypes(phenotypes.map((p) => (p.id === termId ? { ...p, type: newType } : p)))
  }

  const typeLabels: Record<'key' | 'additional' | 'excluded', string> = {
    key: 'Key',
    additional: 'Additional',
    excluded: 'Pertinent Negative',
  }

  const TermRow = ({ term }: { term: HPOTerm }) => (
    <div className="flex w-full items-center gap-3 border-b border-gray-200 py-3 last:border-b-0">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-base font-medium text-gray-900">{term.name}</span>
        {term.hpoCode && <span className="text-sm text-gray-500">{term.hpoCode}</span>}
      </div>

      <div className="flex shrink-0 items-center">
        {(['key', 'additional', 'excluded'] as const).map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => handleTypeChange(term.id, type)}
            className={`border px-3 py-1.5 text-sm font-medium transition-colors first:rounded-l-lg last:rounded-r-lg ${
              term.type === type
                ? 'border-brand-200 bg-brand-50 text-brand-700'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {typeLabels[type]}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => handleRemove(term.id)}
        className="shrink-0 p-1 text-gray-400 hover:text-gray-600"
      >
        <Close className="size-4" />
      </button>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Indication for Testing"
      size="lg"
      hidden={searchModalOpen}
      footer={
        <ModalFooter
          onDiscard={handleDiscard}
          onClose={handleClose}
          lastSaveTime={lastSaveTime}
          isSaving={isSaving}
          onContinue={onContinue ? handleContinue : undefined}
          continueLabel={continueLabel}
        />
      }
    >
      <div className="flex flex-col gap-6">
        <AlertBanner useInflectionIcon={true}>
          {generatedAt
            ? `Phenotypes generated from Clinical Summary at ${generatedAt}`
            : 'Phenotypes generated from Clinical Summary.'}
        </AlertBanner>

        {/* Sticky so the Add Phenotypes launcher stays reachable while the
            phenotype list scrolls. The negative top offset + padding cancels the
            modal body's `py-*` so the button sits flush against the scroll edge,
            and the white background band hides rows scrolling underneath. */}
        <div className="sticky -top-4 z-10 -mt-2 bg-white pb-2 pt-2 dark:bg-gray-900 sm:-top-6">
          <button
            type="button"
            onClick={() => setSearchModalOpen(true)}
            className="inline-flex items-center gap-2 self-start rounded-full bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
          >
            <Plus className="size-4" />
            Add Phenotypes
          </button>
        </div>

        {phenotypes.length > 0 && (
          <div className="flex flex-col border-t border-gray-200">
            {phenotypes.map((term) => (
              <TermRow key={term.id} term={term} />
            ))}
          </div>
        )}

        <HPOSearchModal
          isOpen={searchModalOpen}
          onClose={() => setSearchModalOpen(false)}
          onSelect={handleSearchModalSelect}
          onRemove={handleRemove}
          selectedTermIds={phenotypes.map((p) => p.id)}
          selectedTerms={phenotypes}
          showSelectedTerms={true}
          minQueryLength={3}
        />
      </div>
    </Modal>
  )
}
